import { type Dispatch, type ReactNode, type SetStateAction, createContext, useContext, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import type { Title } from '../data/dataTypes'
import { createApiClient } from '../data/api'
import {
  GENERIC_LOADING_STATES,
  type GenericLoadingState,
  type MovieCategoryOptions,
} from '../constants/globalConstants'
import type { CategoryOptions } from '../components/Header/HeaderOptions'

interface MovieDatabaseContextHandelers {
  readonly setMovieDatabaseContextData: Dispatch<SetStateAction<UserInteractions>>
}

export interface UserInteractions {
  readonly activeCategory: CategoryOptions
  readonly overviewQuery: MovieCategoryOptions | null
  readonly searchTerm: string
}

export type MovieDatabaseData =
  | {
      readonly movieDatabaseLoadingState: GenericLoadingState & 'idle'
      readonly allMovies: null
    }
  | {
      readonly movieDatabaseLoadingState: GenericLoadingState & 'pending'
      readonly allMovies: null
    }
  | {
      readonly movieDatabaseLoadingState: GenericLoadingState & 'error'
      readonly allMovies: null
      readonly errorReason: 'validation_error' | 'unknown_error'
    }
  | {
      readonly movieDatabaseLoadingState: GenericLoadingState & 'success'
      readonly allMovies: ReadonlyArray<Title>
    }

export type MovieDatabaseContextData = MovieDatabaseData & UserInteractions

type MovieDatabaseContextProps = MovieDatabaseContextData & MovieDatabaseContextHandelers

const defaultUserInteractions: UserInteractions = {
  activeCategory: '',
  overviewQuery: null,
  searchTerm: '',
}

export const MovieDatabaseContext = createContext<MovieDatabaseContextProps | undefined>(undefined)

export function useMovieDatabaseContext(): MovieDatabaseContextProps {
  const context = useContext(MovieDatabaseContext)
  if (context === undefined) {
    throw new Error('useMovieDatabaseContext must be used within a MovieProvider')
  }
  return context
}

const apiClient = createApiClient()

async function fetchTitles(): Promise<ReadonlyArray<Title>> {
  const result = await apiClient.getMovies()
  if (!result.ok) throw new Error(result.reason)
  return result.data
}

interface MovieProviderProps {
  readonly children: ReactNode
}

export function MovieProvider({ children }: MovieProviderProps) {
  const [userInteractions, setMovieDatabaseContextData] = useState<UserInteractions>(defaultUserInteractions)

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['titles'],
    queryFn: fetchTitles,
  })

  let serverState: MovieDatabaseData
  if (isPending) {
    serverState = { movieDatabaseLoadingState: GENERIC_LOADING_STATES.pending, allMovies: null }
  } else if (isError) {
    const reason = error.message === 'validation_error' ? 'validation_error' : 'unknown_error'
    serverState = { movieDatabaseLoadingState: GENERIC_LOADING_STATES.error, allMovies: null, errorReason: reason }
  } else {
    serverState = { movieDatabaseLoadingState: GENERIC_LOADING_STATES.success, allMovies: data }
  }

  const contextValue: MovieDatabaseContextProps = {
    ...serverState,
    ...userInteractions,
    setMovieDatabaseContextData,
  }

  return <MovieDatabaseContext.Provider value={contextValue}>{children}</MovieDatabaseContext.Provider>
}
